import { useState, useEffect, useCallback } from 'react';
import {
  LocalNotifications,
  type ScheduleOn,
} from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';
import { useAuth } from './index';
import type { Reminder, ReminderType } from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NewReminderPayload {
  pet_id: string;
  title: string;
  reminder_type: ReminderType;
  due_date: string;           // ISO 8601 datetime string
  recurring?: ReminderCadence;
}

export type ReminderCadence = 'once' | 'daily' | 'weekly' | 'monthly';

interface UseRemindersReturn {
  reminders: Reminder[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addReminder: (payload: NewReminderPayload) => Promise<Reminder>;
  completeReminder: (id: string, notifId: number) => Promise<void>;
  deleteReminder: (id: string, notifId: number) => Promise<void>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Derive a stable numeric notification ID from a UUID.
 * Capacitor requires integer IDs; we use the first 8 hex chars of the UUID.
 */
function uuidToNotifId(uuid: string): number {
  return parseInt(uuid.replace(/-/g, '').slice(0, 8), 16);
}

const CHANNEL_ID = 'pet-reminders';

/** Create the Android notification channel (no-op on iOS / web). */
async function ensureChannel() {
  if (!Capacitor.isNativePlatform()) return;
  await LocalNotifications.createChannel({
    id: CHANNEL_ID,
    name: 'Pet Reminders',
    description: 'Medication and vaccination reminders for your pets',
    importance: 4,     // HIGH
    visibility: 1,     // PUBLIC
    vibration: true,
    sound: 'default',
  });
}

/**
 * Schedule a local notification for a reminder.
 *
 * `allowWhileIdle: true` is REQUIRED on Android so the notification fires
 * even when the device is in Doze / battery-saving mode.
 */
async function scheduleNotification(
  reminder: Reminder,
  cadence: ReminderCadence = 'once'
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return; // Silently skip on web

  const perm = await LocalNotifications.requestPermissions();
  if (perm.display !== 'granted') return;

  await ensureChannel();

  const dueDate = new Date(reminder.due_date);

  // Build the `every` value for recurring notifications
  const cadenceMap: Record<ReminderCadence, ScheduleOn['day'] | undefined> = {
    once:    undefined,
    daily:   undefined,
    weekly:  undefined,
    monthly: undefined,
  };
  // Capacitor uses `every` string for repeating
  const everyMap: Record<ReminderCadence, string | undefined> = {
    once:    undefined,
    daily:   'day',
    weekly:  'week',
    monthly: 'month',
  };

  void cadenceMap; // silence unused-var warning

  await LocalNotifications.schedule({
    notifications: [
      {
        id: uuidToNotifId(reminder.id),
        channelId: CHANNEL_ID,
        title: '🐾 PetNoter Reminder',
        body: reminder.title,
        schedule: {
          at: dueDate,
          allowWhileIdle: true,          // ← Critical: fires during Android Doze mode
          repeats: cadence !== 'once',
          ...(cadence !== 'once' && { every: everyMap[cadence] as never }),
        },
        sound: 'default',
        smallIcon: 'ic_stat_icon_config_sample',
        iconColor: '#0da5a5',
      },
    ],
  });
}

/** Cancel a previously scheduled notification. */
async function cancelNotification(notifId: number): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await LocalNotifications.cancel({ notifications: [{ id: notifId }] });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useReminders(petId: string): UseRemindersReturn {
  const { user } = useAuth();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReminders = useCallback(async () => {
    if (!user || !petId) return;
    setIsLoading(true);
    setError(null);

    const { data, error: err } = await supabase
      .from('reminders')
      .select('*')
      .eq('pet_id', petId)
      .order('due_date', { ascending: true });

    if (err) setError(err.message);
    else setReminders((data as Reminder[]) ?? []);
    setIsLoading(false);
  }, [user, petId]);

  useEffect(() => { void fetchReminders(); }, [fetchReminders]);

  // ── addReminder ────────────────────────────────────────────────────────────
  const addReminder = useCallback(
    async ({ pet_id, title, reminder_type, due_date, recurring = 'once' }: NewReminderPayload): Promise<Reminder> => {
      if (!user) throw new Error('Not authenticated');

      const { data, error: err } = await supabase
        .from('reminders')
        .insert({
          pet_id,
          title,
          reminder_type,
          due_date,
          is_completed: false,
        })
        .select()
        .single();

      if (err) throw new Error(err.message);

      const created = data as Reminder;

      // Schedule the local notification (native only)
      await scheduleNotification(created, recurring);

      setReminders((prev) => [...prev, created].sort(
        (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      ));
      return created;
    },
    [user]
  );

  // ── completeReminder ───────────────────────────────────────────────────────
  const completeReminder = useCallback(async (id: string, notifId: number) => {
    const { error: err } = await supabase
      .from('reminders')
      .update({ is_completed: true })
      .eq('id', id);

    if (err) throw new Error(err.message);

    await cancelNotification(notifId);
    setReminders((prev) =>
      prev.map((r) => (r.id === id ? { ...r, is_completed: true } : r))
    );
  }, []);

  // ── deleteReminder ─────────────────────────────────────────────────────────
  const deleteReminder = useCallback(async (id: string, notifId: number) => {
    const { error: err } = await supabase.from('reminders').delete().eq('id', id);
    if (err) throw new Error(err.message);

    await cancelNotification(notifId);
    setReminders((prev) => prev.filter((r) => r.id !== id));
  }, []);

  return { reminders, isLoading, error, refetch: fetchReminders, addReminder, completeReminder, deleteReminder };
}
